import React from "react";
import { useLocation } from "react-router-dom";
import {
  SITE_URL,
  DEFAULT_META,
  ROUTE_META,
  NOINDEX_PREFIXES,
} from "./meta";

// Organization structured data — shown on every public page.
const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  name: "NIECCE — National Institute of Early Childhood Care and Education",
  alternateName: "NIECCE",
  url: SITE_URL,
  logo: `${SITE_URL}/logo512.png`,
  telephone: "+91 98868 55700",
  email: "info@niecce.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Bengaluru",
    addressRegion: "Karnataka",
    addressCountry: "IN",
  },
  sameAs: ["https://www.youtube.com/@nieccentt6307"],
};

/**
 * SeoManager — single mount point for per-route SEO.
 * Relies on React 19's native hoisting of <title>/<meta>/<link>
 * elements into <head>. No external helmet library required.
 */
const SeoManager = () => {
  const { pathname } = useLocation();

  // Normalize: strip trailing slash (except root)
  const path =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;

  const noindex = NOINDEX_PREFIXES.some(
    (p) => path === p || path.startsWith(p + "/")
  );

  const meta = ROUTE_META[path] || DEFAULT_META;
  const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;

  const courseSchema = meta.courseSchema
    ? {
        "@context": "https://schema.org",
        "@type": "Course",
        name: meta.courseSchema.name,
        description: meta.courseSchema.description,
        provider: {
          "@type": "EducationalOrganization",
          name: "NIECCE",
          sameAs: SITE_URL,
        },
      }
    : null;

  return (
    <>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      {meta.keywords && (
        <meta name="keywords" content={meta.keywords.join(", ")} />
      )}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <link rel="canonical" href={canonical} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="NIECCE" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={`${SITE_URL}/logo512.png`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={`${SITE_URL}/logo512.png`} />

      {/* Structured data */}
      {!noindex && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ORG_SCHEMA) }}
        />
      )}
      {!noindex && courseSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(courseSchema) }}
        />
      )}
    </>
  );
};

export default SeoManager;
